import { useNavigate } from "@tanstack/react-router";
import { useI18nContext } from "@i18n/i18n-react";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { GalleryHorizontal, LayoutGrid } from "lucide-react";
import { useState } from "react";
import type { HubService } from "./__generated__/HomePageDataQuery.graphql";
import type { HubFilterService as Service } from "./layout/HubMap/hubFilters";

export type HomeCardSectionItem = {
	id: string;
	title: string;
	description: string;
	imageSrc: string;
	badge?: string;
	to?: string;
	/** Service used to filter the hub map when selecting a hub for this card. */
	service?: Service;
	/**
	 * Service that actually gates this card's availability, when it differs from `service`.
	 * E.g. Shop & Collect is offered wherever CLICK_N_COLLECT is available.
	 */
	availabilityService?: HubService;
};

type HomeCardSectionProps = {
	title: string;
	subtitle: string;
	switchToGridLabel: string;
	switchToCarouselLabel: string;
	items: HomeCardSectionItem[];
	onSelectHub: (service?: Service, to?: string) => void;
	scannedHub?: {
		name: string;
	} | null;
	scannedHubServices?: readonly HubService[] | null;
};

const cardsContainerClass = (isGrid: boolean) =>
	isGrid
		? "grid grid-cols-2 gap-x-2 gap-y-4 lg:flex lg:w-full lg:gap-2 lg:overflow-x-auto lg:pb-1"
		: "flex w-[calc(100%+1.5rem)] gap-2 overflow-x-auto pb-1 lg:w-full";

const HomeCard = ({
	item,
	isGrid,
	onSelectHub,
	scannedHub,
	scannedHubServices,
}: {
	item: HomeCardSectionItem;
	isGrid: boolean;
	onSelectHub: (service?: Service, to?: string) => void;
	scannedHub?: {
		name: string;
	} | null;
	scannedHubServices?: readonly HubService[] | null;
}) => {
	const navigate = useNavigate();
	const i18n = useI18nContext().LL["new-ui"];
	const itemContainerClass = isGrid ? "min-w-0 w-full lg:w-[167px] lg:shrink-0" : "w-[calc((100vw-3rem-0.5rem)/2)] shrink-0 lg:w-[167px]";

	// When a hub is selected but it doesn't offer this service, the service is
	// still available in other hubs: show a badge and let the user pick another hub.
	// Availability can be gated by a different service than the one used for filtering
	// (e.g. Shop & Collect depends on CLICK_N_COLLECT).
	const availabilityService = item.availabilityService ?? item.service;
	const availableElsewhere = Boolean(scannedHub && availabilityService && !(scannedHubServices?.includes(availabilityService) ?? false));
	const displayBadge = item.badge ?? (availableElsewhere ? i18n.HOME_AVAILABLE_OTHER_HUBS() : undefined);

	const content = (
		<div id={item.id} className="w-full space-y-3">
			<div className="relative aspect-[167/164] w-full overflow-hidden rounded-xl bg-muted shadow-sm">
				<img src={item.imageSrc} alt="" className={`h-full w-full object-cover ${displayBadge ? "opacity-50" : ""}`} />
				{displayBadge && (
					<Badge className="absolute left-2 top-2 rounded-full bg-background text-foreground hover:bg-background">{displayBadge}</Badge>
				)}
			</div>
			<div className="px-2">
				<p className="line-clamp-1 font-breit text-base font-medium leading-4 text-card-foreground">{item.title}</p>
				<p className="line-clamp-2 text-sm leading-5 text-muted-foreground">{item.description}</p>
			</div>
		</div>
	);

	// `item.badge` cards (e.g. coming soon) are inert; cards without a target stay static.
	if (item.badge || (!item.to && !item.service)) {
		return <div className={itemContainerClass}>{content}</div>;
	}

	const handleClick = () => {
		// No hub selected, or hub selected but service not available here:
		// open the hub selector filtered on this service.
		if ((!scannedHub || availableElsewhere) && item.service) {
			onSelectHub(item.service, item.to);
			return;
		}

		if (!item.to) {
			return;
		}

		if (scannedHub && item.to.includes("$hubMetaName")) {
			navigate({ to: item.to as any, params: { hubMetaName: scannedHub.name } as any });
			return;
		}

		navigate({ to: item.to as any });
	};

	return (
		<button type="button" className={`${itemContainerClass} block text-left`} onClick={handleClick}>
			{content}
		</button>
	);
};

export const HomeCardSection = (props: HomeCardSectionProps) => {
	const { title, subtitle, switchToGridLabel, switchToCarouselLabel, items, onSelectHub, scannedHub, scannedHubServices } = props;
	const [isGrid, setIsGrid] = useState(false);

	return (
		<div className="space-y-4 px-6 pt-6">
			<div className="flex items-start justify-between">
				<div className="max-w-sm space-y-1">
					<h2 className="font-breit text-xl font-medium leading-7 text-black">{title}</h2>
					<p className="text-sm leading-5 text-foreground">{subtitle}</p>
				</div>
				{items.length > 2 ? (
					<Button
						variant="outline"
						size="icon"
						className="size-10 shrink-0 lg:hidden"
						onClick={() => setIsGrid((prev) => !prev)}
						aria-label={isGrid ? switchToCarouselLabel : switchToGridLabel}
					>
						{isGrid ? <GalleryHorizontal className="size-4" /> : <LayoutGrid className="size-4" />}
					</Button>
				) : null}
			</div>
			<div className={cardsContainerClass(isGrid)}>
				{items.map((item) => (
					<HomeCard
						key={item.id}
						item={item}
						isGrid={isGrid}
						onSelectHub={onSelectHub}
						scannedHub={scannedHub}
						scannedHubServices={scannedHubServices}
					/>
				))}
			</div>
		</div>
	);
};
